import { useEffect, useState } from "react";
import { Star, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/api/client";
import { toast } from "sonner";

function Stars({ value = 0 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`h-3.5 w-3.5 ${i <= Math.round(value) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
      ))}
    </span>
  );
}

function ReviewRow({ review, onReplied }) {
  const [reply, setReply] = useState(review.vendor_reply || "");
  const [editing, setEditing] = useState(!review.vendor_reply);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const text = reply.trim();
    if (!text) return toast.error("Type a reply first.");
    setBusy(true);
    try {
      await api.post(`/exhibitor/reviews/${review.id}/reply`, { reply: text });
      toast.success("Reply posted");
      setEditing(false);
      onReplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post reply");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`vendor-review-${review.id}`} className="p-4 bg-white border border-gray-200 rounded-2xl">
      <div className="flex gap-3">
        {review.product_image ? (
          <img src={review.product_image} alt={review.product_name} className="h-12 w-12 rounded-lg object-cover flex-shrink-0" />
        ) : <div className="h-12 w-12 rounded-lg bg-gray-100 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{review.product_name || "Product"}</div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <Stars value={review.rating} />
            <span>{review.author || "Verified buyer"}</span>
            <span className="text-gray-400">· {new Date(review.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
          </div>
          <p className="mt-2 text-sm text-gray-800 leading-snug">{review.body}</p>

          {review.vendor_reply && !editing && (
            <div data-testid={`vendor-reply-${review.id}`} className="mt-3 rounded-xl bg-[#FEF3E6] border border-[#F48E2E]/30 p-2.5">
              <div className="text-[11px] uppercase tracking-widest text-[#F48E2E] font-bold mb-1 flex items-center gap-1">
                <MessageCircle className="h-3 w-3" /> Your reply
                <span className="text-gray-400 font-normal normal-case ml-1">{review.vendor_reply_at ? new Date(review.vendor_reply_at).toLocaleDateString("en-IN") : ""}</span>
              </div>
              <p className="text-sm text-gray-800">{review.vendor_reply}</p>
              <button
                data-testid={`vendor-reply-edit-${review.id}`}
                onClick={() => setEditing(true)}
                className="mt-1.5 text-[11px] text-[#F48E2E] hover:underline"
              >Edit reply</button>
            </div>
          )}

          {editing && (
            <div className="mt-3 flex gap-2 items-start">
              <Input
                data-testid={`vendor-reply-input-${review.id}`}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Thank the buyer or address the concern (240 chars max)"
                maxLength={240}
                className="h-10 text-sm"
              />
              <Button
                data-testid={`vendor-reply-send-${review.id}`}
                onClick={save}
                disabled={busy}
                className="h-10 rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-xs whitespace-nowrap"
              >
                <Send className="h-3.5 w-3.5 mr-1" /> {busy ? "Sending…" : review.vendor_reply ? "Update" : "Reply"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VendorReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/exhibitor/reviews")
      .then((r) => setReviews(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <div className="text-gray-500">Loading reviews…</div>;
  if (reviews.length === 0) {
    return <div className="text-center py-16 text-gray-500" data-testid="vendor-reviews-empty">No reviews yet. Once a buyer marks a delivered order and posts a review, it'll show up here.</div>;
  }
  const pendingReply = reviews.filter((r) => !r.vendor_reply).length;
  const avg = reviews.length ? (reviews.reduce((a, r) => a + Number(r.rating || 0), 0) / reviews.length).toFixed(1) : "0.0";

  return (
    <div className="space-y-3" data-testid="vendor-reviews-tab">
      <div className="flex flex-wrap justify-between items-center gap-2 p-4 bg-white border border-gray-200 rounded-2xl">
        <div>
          <div className="font-display text-lg">Customer Reviews</div>
          <div className="text-xs text-gray-500">Reply to each review in one line to thank buyers or address concerns. Replies show publicly under the review.</div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div><span className="text-gray-500">Average:</span> <span className="font-medium">{avg} <Stars value={avg} /></span></div>
          <div><span className="text-gray-500">Awaiting reply:</span> <span className="font-medium text-amber-700" data-testid="vendor-reviews-pending">{pendingReply}</span></div>
        </div>
      </div>
      {reviews.map((r) => <ReviewRow key={r.id} review={r} onReplied={load} />)}
    </div>
  );
}
